/**
 * Downloading an export.
 *
 * The endpoint needs the Authorization header, so an export cannot be a plain
 * link — it is fetched as a blob and handed to the browser as an object URL.
 * That detail is the same everywhere an export is offered, so it lives here
 * rather than in each page that offers one.
 */
import { client } from '../api/client';

export type ExportFormat = 'csv' | 'xlsx';

export interface ExportRequest {
  kind: string;
  format?: ExportFormat;
  /**
   * Any filters the endpoint accepts, in camelCase — the interceptor converts.
   *
   * Booleans and numbers as well as strings, because the screens' own list
   * queries send those: mineOnly is a checkbox and year is a number, and
   * stringifying them at each call site is a conversion to get wrong.
   */
  params?: Record<string, string | number | boolean | undefined>;
}

/**
 * Read the message out of an error whose body is a Blob.
 *
 * With responseType 'blob' even a 422 arrives as binary, so the ordinary error
 * helper sees an unreadable body and falls back to something generic — losing
 * the one message that tells the user what to do next ("narrow it with a date
 * range").
 */
export async function exportErrorMessage(
  error: unknown,
  fallback = 'Could not build that export.',
): Promise<string> {
  const body = (error as { response?: { data?: unknown } })?.response?.data;
  if (body instanceof Blob) {
    try {
      const parsed = JSON.parse(await body.text()) as { detail?: unknown };
      if (typeof parsed.detail === 'string') return parsed.detail;
    } catch {
      // Not JSON — fall through to the generic message.
    }
  }
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response
    ?.data?.detail;
  return typeof detail === 'string' ? detail : fallback;
}

/** Use the filename the server chose rather than inventing one here. */
function filenameFrom(disposition: string | undefined, fallback: string): string {
  const match = disposition?.match(/filename="?([^";]+)"?/);
  return match?.[1] ?? fallback;
}

/**
 * GET a binary endpoint and hand the body to the browser as a download.
 *
 * The whole reason this cannot be an ordinary link: the endpoint needs the
 * Authorization header, so the file has to be fetched and then handed over as
 * an object URL. Shared rather than repeated per caller — an export and an
 * invoice PDF differ only in their URL, and a second copy of this is a second
 * place to forget revokeObjectURL.
 *
 * Resolves with the filename saved, so a caller can name it in a toast.
 */
export async function downloadFile(
  url: string,
  { params = {}, fallbackName }: {
    params?: Record<string, string | undefined>;
    fallbackName: string;
  },
): Promise<string> {
  const response = await client.get(url, {
    responseType: 'blob',
    params: Object.fromEntries(
      Object.entries(params).filter(([, value]) => value),
    ),
  });

  const name = filenameFrom(
    response.headers['content-disposition'] as string | undefined,
    fallbackName,
  );

  const objectUrl = URL.createObjectURL(response.data as Blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = name;
  anchor.click();
  URL.revokeObjectURL(objectUrl);
  return name;
}

/**
 * Fetch an export and hand it to the browser as a download.
 *
 * Resolves with the filename saved, so a caller can name it in a toast.
 */
export async function downloadExport({
  kind,
  format = 'csv',
  params = {},
}: ExportRequest): Promise<string> {
  return downloadFile(`/api/v1/exports/${kind}`, {
    params: { ...(format === 'xlsx' ? { format } : {}), ...params },
    fallbackName: `${kind}.${format}`,
  });
}

/**
 * Download a raised invoice as the document that gets sent to the client.
 *
 * Refused by the server for a draft, which has no fixed figures yet — so the
 * caller should only offer this where the invoice has been raised.
 */
export async function downloadInvoicePdf(
  invoiceId: string,
  invoiceNumber: string,
): Promise<string> {
  return downloadFile(`/api/v1/admin/billing/invoices/${invoiceId}/pdf`, {
    // Slashes are legal in an invoice number and not in a filename. The
    // server sends its own Content-Disposition; this only matters if that
    // header goes missing.
    fallbackName: `invoice-${invoiceNumber.replace(/[/\\]/g, '-')}.pdf`,
  });
}


/**
 * Download a candidate's profile as the document that gets sent to the client.
 *
 * Keyed on the submission, not the candidate: a candidate can be with two
 * clients at once, and the document names the client, the role and the date
 * from the submission it was built for. Offering this per candidate would
 * make "which client's copy is this" unanswerable.
 */
export async function downloadCandidateProfilePdf(
  submissionId: string,
  candidateName: string,
): Promise<string> {
  return downloadFile(`/api/v1/submissions/${submissionId}/profile.pdf`, {
    // The server sends its own Content-Disposition and sanitises the name
    // there; this only matters if that header goes missing.
    fallbackName: `${candidateName.replace(/[^A-Za-z0-9]+/g, '-')}-profile.pdf`,
  });
}
